import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UsersIcon, SendIcon, ArrowRightIcon, CheckIcon, MailIcon } from 'lucide-react';
const tabs = [{
  id: 'search',
  icon: SearchIcon,
  title: 'Search the local business database',
  description: 'Filter through 200M+ local businesses by location, category, revenue, and tech stack. The ultimate local business owner finder for your sales team.'
}, {
  id: 'contacts',
  icon: UsersIcon,
  title: 'Access business owner emails',
  description: 'Unlike other platforms, we provide verified business owner emails and direct dials. See exactly who you are contacting before you spend credits.'
}, {
  id: 'sequence',
  icon: SendIcon,
  title: 'Built-in email sequencing',
  description: 'Stop exporting CSVs. Launch personalized, multi-step email sequences directly from our local business database to your target prospects.'
}];
export function DatabaseWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-4119">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4120">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-4121">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-4122">
            The complete{' '}
            <span className="text-blue-600" data-id="element-4123">local business owner finder</span>{' '}
            workflow
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-4124">
            From discovering local small businesses to booking meetings —
            everything happens in one unified platform.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-4125">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-4126">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-4127">
                  <div className="flex items-start gap-6" data-id="element-4128">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-4129">
                      <tab.icon className="w-6 h-6" data-id="element-4130" />
                    </div>
                    <div data-id="element-4131">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-4132">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-4133">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-4134">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-4135">
              <AnimatePresence mode="wait" data-id="element-4136">
                {activeTab === 'search' && <motion.div key="search" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100" data-id="element-4137">
                    <div className="flex items-center justify-between mb-6" data-id="element-4138">
                      <h4 className="font-bold text-slate-900" data-id="element-4139">
                        Local business filters
                      </h4>
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full" data-id="element-4140">
                        12,405 matches
                      </span>
                    </div>
                    <div className="space-y-5" data-id="element-4141">
                      <div data-id="element-4142">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-4143">
                          Location
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-4144">
                          Texas (Statewide)
                        </div>
                      </div>
                      <div data-id="element-4145">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-4146">
                          Industry
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-4147">
                          HVAC & Plumbing
                        </div>
                      </div>
                      <div data-id="element-4148">
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-4149">
                          Employee count
                        </label>
                        <div className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 border border-slate-100" data-id="element-4150">
                          10 - 50 employees
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'contacts' && <motion.div key="contacts" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-4151">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-4152">
                      <div className="bg-slate-50 px-5 py-3 border-b border-slate-100 flex justify-between items-center" data-id="element-4153">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-4154">
                          Business employee email lists
                        </span>
                        <span className="text-xs text-blue-600 font-medium" data-id="element-4155">
                          Viewable before export
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-4156">
                        {[{
                      name: 'John Smith',
                      role: 'Owner / Founder',
                      email: 'john@smithhvac.com'
                    }, {
                      name: 'Sarah Davis',
                      role: 'Operations Manager',
                      email: 'sarah.d@smithhvac.com'
                    }, {
                      name: 'Mike Wilson',
                      role: 'Lead Technician',
                      email: 'mike@smithhvac.com'
                    }].map((contact, i) => <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-4157">
                            <div className="flex items-center gap-3" data-id="element-4158">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs" data-id="element-4159">
                                {contact.name.charAt(0)}
                              </div>
                              <div data-id="element-4160">
                                <div className="text-sm font-bold text-slate-900" data-id="element-4161">
                                  {contact.name}
                                </div>
                                <div className="text-xs text-slate-500" data-id="element-4162">
                                  {contact.role}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-4163">
                              <CheckIcon className="w-4 h-4 text-green-500" data-id="element-4164" />
                              <span className="text-sm text-slate-600" data-id="element-4165">
                                {contact.email}
                              </span>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'sequence' && <motion.div key="sequence" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-4166">
                    <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" data-id="element-4167">
                      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100" data-id="element-4168">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-4169">
                          <SendIcon className="w-5 h-5 text-blue-600" data-id="element-4170" />
                        </div>
                        <div data-id="element-4171">
                          <h4 className="font-bold text-slate-900" data-id="element-4172">
                            HVAC Owners Campaign
                          </h4>
                          <p className="text-xs text-slate-500" data-id="element-4173">
                            Active • 450 prospects
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-4174">
                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-4175">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-100 text-blue-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-4176">
                            <MailIcon className="w-4 h-4" data-id="element-4177" />
                          </div>
                          <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-4178">
                            <div className="text-xs font-bold text-slate-900 mb-1" data-id="element-4179">
                              Step 1: Initial email
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-4180">
                              Sent immediately
                            </div>
                          </div>
                        </div>

                        <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-4181">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-slate-100 text-slate-400 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-4182">
                            <MailIcon className="w-4 h-4" data-id="element-4183" />
                          </div>
                          <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-3 rounded-xl border border-slate-100" data-id="element-4184">
                            <div className="text-xs font-bold text-slate-900 mb-1" data-id="element-4185">
                              Step 2: Follow up
                            </div>
                            <div className="text-[10px] text-slate-500" data-id="element-4186">
                              Wait 3 days
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}